import { useEffect, useState } from 'react';
import classNames from 'classnames';

import { useAppDispatch, useAppSelector } from '@/state/store';
import { setIsLocalePickerPanelVisible } from '@/state/features/app';
import { useLang } from '@/components/TranslatedText';
import Flag from '@/components/Flag';
import { getSavedLangIso, saveLangIso } from '@/utils/localLang';
import { DEFAULT_LANG, IS_DISCORD_ACTIVITY } from '@/globals';
import { sendEventV2 } from '@/utils/analytics';

export const LOCALE_PICKER_HEIGHT = 50;
export const LOCALE_PICKER_ANIM_DURATION = '0.3s';

import * as styles from './styles.module.scss';

export default function LocalePickerPanel() {
    const dispatch = useAppDispatch();
    const isVisible = useAppSelector((state) => state.app.isLocalePickerPanelVisible);
    const { langs, current, setLang } = useLang();
    const [hasCheckedInitial, setHasCheckedInitial] = useState(false);

    // Show picker automatically on first visit if translations enabled and no saved preference
    useEffect(() => {
        if (hasCheckedInitial) return;
        if (process.env.PUBLIC_SUPPORT_TRANSLATIONS !== 'true') return;

        const savedLang = getSavedLangIso();
        if (!savedLang) {
            // Don't open picker initially on Discord
            if (IS_DISCORD_ACTIVITY) return;
            dispatch(setIsLocalePickerPanelVisible(true));
        } else if (savedLang !== current) {
            setLang(savedLang);
        }
        setHasCheckedInitial(true);
    }, [hasCheckedInitial]);

    function handleLocaleClick(langISO: string) {
        setLang(langISO);
        saveLangIso(langISO);
        dispatch(setIsLocalePickerPanelVisible(false));
    }

    useEffect(() => {
        if (current && langs?.length) {
            // If recieve locale not in supported langs (e.g from discord), set locale to default
            const matchingLang = langs.some((lang) => lang.langISO === current);
            if (!matchingLang && current !== DEFAULT_LANG.langISO) {
                console.warn(
                    `${current} not found in list of languages. Setting language to default: ${DEFAULT_LANG.langISO}`,
                );
                setLang(DEFAULT_LANG.langISO);
                return;
            }
            sendEventV2('arise_locale', [current]);
        }
    }, [current, langs]);

    // If no languages, show default lang in picker
    const flags = langs?.length ? langs : [DEFAULT_LANG];

    return (
        <div
            className={styles.LocalePickerPanel}
            inert={!isVisible ? '' : undefined}
            style={{
                height: LOCALE_PICKER_HEIGHT,
                transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
                transition: `transform ${LOCALE_PICKER_ANIM_DURATION} ease`,
            }}
        >
            <div className={styles.flagsContainer}>
                {flags.map(({ langISO, displayName }) => (
                    <button
                        key={langISO}
                        className={classNames(styles.flagButton, {
                            [styles.isSelected]: langs?.length ? langISO === current : true,
                        })}
                        onClick={() => handleLocaleClick(langISO)}
                        title={displayName}
                        aria-label={displayName}
                    >
                        <Flag countryCode={langISO} className={styles.flag} />
                    </button>
                ))}
            </div>
        </div>
    );
}
